import React, { useState, useCallback, useRef, useEffect } from 'react';
import useChannels from '../../hooks/useChannels';
import ChannelSidebar from './ChannelSidebar';
import MessagePane from './MessagePane';
import ThreadPanel from './ThreadPanel';
import ChannelCreateModal from './ChannelCreateModal';
import ChannelSettingsModal from './ChannelSettingsModal';
import ChannelMembersModal from './ChannelMembersModal';

// @context TGW-1860 現在の URL からチャンネル（チャットルーム）キーを読む。
//   ?channel=<key> が無ければ既定の general。
function channelKeyFromUrl() {
  return (
    new URLSearchParams(window.location.search).get('channel') || 'general'
  );
}

// @context TGW-1860 選択キーに対応する URL（pathname は維持し、query の channel だけ更新）。
//   general は既定のため channel クエリを付けず、他は ?channel=<key> を付ける。
function urlForChannelKey(key) {
  const url = new URL(window.location.href);
  if (key && key !== 'general') {
    url.searchParams.set('channel', key);
  } else {
    url.searchParams.delete('channel');
  }
  return url.pathname + url.search + url.hash;
}

// @context TGW-1769 Slack風サークルチャットの最上位。3ペイン（サイドバー/メッセージ/スレッド）の
//   レイアウト殻と「選択中チャンネル・開いているスレッド・モーダル」の状態だけを持つ。
//   狭幅（<=768px）はサイドバー=ドロワー、スレッド=全画面オーバーレイ（Slackモバイル準拠）。
export default function SlackCircleChat({
  circleId,
  circleName,
  currentUser,
  isWebViewTabApp,
}) {
  const { channels, canManage, loading, error, refetch, markChannelRead } =
    useChannels(circleId);
  // @context TGW-1860 チャンネル（チャットルーム）ごとに URL を変える。
  //   /message/circle/:id?channel=<key>（general は既定のためクエリ無し）。
  //   これでルーム移動が URL に反映され、リロード/共有で同じルームを開ける。
  const [selectedKey, setSelectedKey] = useState(channelKeyFromUrl);
  const [openThreadRoot, setOpenThreadRoot] = useState(null); // スレッドを開いているルートメッセージ
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [settingsChannel, setSettingsChannel] = useState(null);
  const [membersChannel, setMembersChannel] = useState(null);
  // スレッドパネルへWS経由の返信を流すコールバック登録先（MessagePane -> ThreadPanel）
  const threadIncomingRef = useRef(null);

  // @context TGW-1853 PCでスレッドパネルの横幅をドラッグで自由調整する（狭幅=全画面のためCSSで無効化）。
  const [threadWidth, setThreadWidth] = useState(360);
  const resizingRef = useRef(false);
  const startResize = useCallback((e) => {
    e.preventDefault();
    resizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);
  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current) return;
      // パネルは右端に固定。右端(window幅)からカーソルまでの距離が新しい幅。
      const w = window.innerWidth - e.clientX;
      setThreadWidth(Math.min(720, Math.max(280, w)));
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // @context TGW-1769 レイアウト共通の丸型戻るボタン(.position--backbtn)がヘッダーからはみ出して
  //   本UIに被るため、本画面表示中だけ body にクラスを付けてCSSでヘッダーバー内に収める
  //   （共通CSSを直接変えると他のメッセージ画面に影響するためスコープする）。
  useEffect(() => {
    document.body.classList.add('slackch-page');
    return () => document.body.classList.remove('slackch-page');
  }, []);

  const selected = channels.find((ch) => ch.key === selectedKey) || channels[0];

  // @context TGW-1860 チャンネルキーを選択し、URL（?channel=）も更新する（履歴に積む）。
  //   モーダルからの自動選択（作成/アーカイブ）も含め、選択は必ずここを通す。
  const selectChannelKey = useCallback((key) => {
    setSelectedKey(key);
    const next = urlForChannelKey(key);
    const current =
      window.location.pathname + window.location.search + window.location.hash;
    if (next !== current) {
      window.history.pushState({ channelKey: key }, '', next);
    }
  }, []);

  // ブラウザの戻る/進むで URL が変わったら選択チャンネルを同期する。
  useEffect(() => {
    const onPopState = () => setSelectedKey(channelKeyFromUrl());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleSelectChannel = useCallback(
    (key) => {
      selectChannelKey(key);
      setOpenThreadRoot(null);
      setDrawerOpen(false);
      markChannelRead(key);
    },
    [markChannelRead, selectChannelKey],
  );

  const handleThreadReplyReceived = useCallback((reply) => {
    if (threadIncomingRef.current) threadIncomingRef.current(reply);
  }, []);

  if (error === 404 || error === 403) {
    return (
      <div className="slackch-error">
        このサークルのメッセージにはアクセスできません
      </div>
    );
  }

  return (
    <div className={`slackch ${isWebViewTabApp ? 'slackch--webview' : ''}`}>
      {drawerOpen && (
        <div
          className="slackch-drawer-overlay"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div
        className={`slackch-sidebar ${drawerOpen ? 'slackch-sidebar--open' : ''}`}
      >
        <ChannelSidebar
          circleName={circleName}
          channels={channels}
          selectedKey={selected && selected.key}
          canManage={canManage}
          loading={loading}
          onSelect={handleSelectChannel}
          onCreateClick={() => setShowCreateModal(true)}
        />
      </div>

      <div className="slackch-main">
        {selected && (
          <MessagePane
            key={selected.key}
            circleId={circleId}
            channel={selected}
            currentUser={currentUser}
            canManage={canManage}
            onOpenDrawer={() => setDrawerOpen(true)}
            onOpenThread={(rootMessage) => setOpenThreadRoot(rootMessage)}
            onOpenSettings={() => setSettingsChannel(selected)}
            onOpenMembers={() => setMembersChannel(selected)}
            onThreadReplyReceived={handleThreadReplyReceived}
            onMessageDeleted={(messageId) =>
              // 開いているスレッドのルートが削除されたら、パネル側も即プレースホルダ表示に切り替える
              setOpenThreadRoot((prev) =>
                prev && prev.id === messageId
                  ? { ...prev, is_deleted: true }
                  : prev,
              )
            }
          />
        )}
      </div>

      {openThreadRoot && selected && (
        <div className="slackch-thread" style={{ width: threadWidth }}>
          <div
            className="slackch-thread-resizer"
            onMouseDown={startResize}
            role="separator"
            aria-orientation="vertical"
            aria-label="スレッドの幅を調整"
          />
          <ThreadPanel
            key={`thread-${openThreadRoot.id}`}
            circleId={circleId}
            channel={selected}
            rootMessage={openThreadRoot}
            currentUser={currentUser}
            canManage={canManage}
            registerIncoming={(fn) => {
              threadIncomingRef.current = fn;
            }}
            onClose={() => {
              setOpenThreadRoot(null);
              threadIncomingRef.current = null;
            }}
          />
        </div>
      )}

      {showCreateModal && (
        <ChannelCreateModal
          circleId={circleId}
          onClose={() => setShowCreateModal(false)}
          onCreated={(created) => {
            setShowCreateModal(false);
            refetch().then(() => {
              if (created && created.id) selectChannelKey(`ch-${created.id}`);
            });
          }}
        />
      )}

      {membersChannel && (
        <ChannelMembersModal
          circleId={circleId}
          channel={membersChannel}
          onClose={() => setMembersChannel(null)}
        />
      )}

      {settingsChannel && (
        <ChannelSettingsModal
          circleId={circleId}
          channel={settingsChannel}
          onClose={() => setSettingsChannel(null)}
          onChanged={(result) => {
            setSettingsChannel(null);
            refetch();
            if (result && result.archived) selectChannelKey('general');
          }}
        />
      )}
    </div>
  );
}
